import CLIENT from '../utils/CUP';
import zxcvbn from 'zxcvbn';
import Logger, {LOG_LEVELS, ALL_MESSAGES} from '../utils/logger';

export const MODULE_NAME = 'USERS_CONTROLLER';
const log = new Logger(MODULE_NAME, LOG_LEVELS.DEBUG);

function namespaced(name) {
  return MODULE_NAME + '.' + name;
}

const LOAD = namespaced('LOAD');
const LOAD_OK = namespaced('LOAD_OK');
const LOAD_FAIL = namespaced('LOAD_FAIL');

// For now we are overloading the use of UPDATE to include delete too.
const UPDATE = namespaced('UPDATE');
const UPDATE_PROGRESS = namespaced('UPDATE_PROGRESS');
const UPDATE_OK = namespaced('UPDATE_OK');
const UPDATE_FAIL = namespaced('UPDATE_FAIL');
const CHECK_PASSWORD_STRENGTH_OK = namespaced('CHECK_PASSWORD_STRENGTH_OK');
const CHECK_PASSWORD_STRENGTH_FAIL = namespaced('CHECK_PASSWORD_STRENGTH_FAIL');
const ROLE_LOAD_OK = namespaced('ROLE_LOAD_OK');
const ROLE_LOAD_FAIL = namespaced('ROLE_LOAD_FAIL');

const WEAK_SCORE = 2;
const OK_SCORE = 3;
const STRENGTH_STATUS = {
    WEAK :'weak',
    OK : 'ok',
    STRONG : 'strong'
}
export const ACTION_TYPES = {
  LOAD,
  LOAD_OK,
  LOAD_FAIL,
  UPDATE,
  UPDATE_PROGRESS,
  UPDATE_OK,
  UPDATE_FAIL,
  CHECK_PASSWORD_STRENGTH_OK,
  CHECK_PASSWORD_STRENGTH_FAIL,
  ROLE_LOAD_OK,
  ROLE_LOAD_FAIL,
};

function getPasswordStrength(password)
{
    return new Promise(function(resolve, reject) {
        var userInputs = [];
        var result = zxcvbn(password, userInputs);
        resolve({'entropyBits' : result.score, 'status' :  result.score < WEAK_SCORE ? STRENGTH_STATUS.WEAK :
                (result.score <= OK_SCORE ? STRENGTH_STATUS.OK : STRENGTH_STATUS.STRONG)});
    });
}
// In redux parlance, this is a reducer
export const updateSubStateFromAction = (state = {
  data: undefined,
  loading: false,
  error: null,
  uploading: false
}, action) => {
  log.debug('updateSubStateFromAction: ', action, state);
  const getNewState = (action, state) => {
    switch (action.type) {
      case LOAD:
        return Object.assign({}, state, {loading: true, error: null});
      case LOAD_OK:
        return Object.assign({}, state, {data: action.response, loading: false, error: null});
      case LOAD_FAIL:
        return Object.assign({}, state, {data: undefined, loading: false, error: action.error});
      case UPDATE:
        return Object.assign({}, state, {loading: true, error: null, progress: action.progress});
      case UPDATE_PROGRESS:
        return Object.assign({}, state, {error: null, progress: action.progress});
      case UPDATE_OK:
        return Object.assign({}, state, {
          updateData: action.response,
          dataToken: action.response,
          loading: false,
          error: null
        });
      case UPDATE_FAIL:
        return Object.assign({}, state, {loading: false, error: action.error});
      case CHECK_PASSWORD_STRENGTH_OK:
        return Object.assign({}, state, {passwordStrengthInfo: action.response, loading: false, error: null});
      case CHECK_PASSWORD_STRENGTH_FAIL:
        return Object.assign({}, state, {passwordStrengthInfo: null, loading: false, error: action.error});
      case ROLE_LOAD_OK:
        return Object.assign({}, state, {roles: action.response, loading: false, error: null});
      case ROLE_LOAD_FAIL:
        return Object.assign({}, state, {roles: null, loading: false, error: action.error});
      default:
        return state;
    }
  };
  const newState = getNewState(action, state);
  log.debug('updateSubStateFromAction:  newState', newState);
  return newState;
};

export const API = {
  doLoad: () => {
    return (dispatch, getState) => {
      dispatch({type: LOAD});
      return CLIENT.getUsers()
        .then((response) => {
          dispatch({type: LOAD_OK, response: response});
        })
        .catch((error) => {
          dispatch({type: LOAD_FAIL, error});
        });
    }
  },
  doCreate: (user) => {
    return (dispatch, getState) => {
      dispatch({type: UPDATE});
      return CLIENT.createUser(user)
        .then((response) => {
          if (response.resetToken) {
            dispatch({type: UPDATE_OK, response: response});
            return CLIENT.getUsers()
              .then((usersResponse) => {
                dispatch({type: LOAD_OK, response: usersResponse})
              })
              .catch((error) => {
                dispatch({type: LOAD_FAIL, error});
              });
          } else {
            dispatch({type: UPDATE_FAIL, error: response});
          }
        })
        .catch((error) => {
          log.debug('doCreate:  catch', error);
          dispatch({type: UPDATE_FAIL, error});
        });
    }
  },
  doUpdate: (id, user) => {
    log.clearDictionaryError(ALL_MESSAGES.USER_UPDATE_UNAUTHORIZED_ERROR);
    log.clearDictionaryError(ALL_MESSAGES.USER_UPDATE_ERROR);
    return (dispatch, getState) => {
      dispatch({type: UPDATE});
      return CLIENT.updateUser(id, user)
        .then((response) => {
          log.debug("doUpdate: response status: ", response.status);
          dispatch({type: UPDATE_OK});
          // dispatch({type: UPDATE_OK, response: response});
          log.setDictionaryMessage(ALL_MESSAGES.USER_UPDATE_SUCCESS);
          return CLIENT.getUsers()
            .then((usersResponse) => {
              dispatch({type: LOAD_OK, response: usersResponse})
            })
            .catch((error) => {
              dispatch({type: LOAD_FAIL, error});
            });
        })
        .catch((error) => {
          log.debug('doUpdate:  catch', error);
          if (error.status == 401)
            log.setDictionaryMessage(ALL_MESSAGES.USER_UPDATE_UNAUTHORIZED_ERROR);
          else
            log.setDictionaryMessage(ALL_MESSAGES.USER_UPDATE_ERROR);
          dispatch({type: UPDATE_FAIL, error});
        });
    }
  },
  doDelete: (id) => {
    // TODO: Consider adding support for deleting multiple users.
    return (dispatch, getState) => {
      dispatch({type: UPDATE});
      return CLIENT.deleteUser(id)
        .then((response) => {
          dispatch({type: UPDATE_OK, response: response});
        })
        .catch((error) => {
          dispatch({type: UPDATE_FAIL, error});
        });
    }
  },
  doUpdatePassword: (id, userPassword, callback = () => {}) => {
    return (dispatch, getState) => {
      dispatch({type: UPDATE});
      return CLIENT.updateUserPassword(id, userPassword.oldpassword, userPassword.newpassword)
        .then((response) => {
          log.debug('doUpdatePassword:  success ', response);

          if ((response.status[0].statusCode).toLowerCase() === 'ok' || (response.status[0].statusCode).toLowerCase() === 'strong') {
            log.setDictionaryMessage(ALL_MESSAGES.NEW_PASSWORD_SET_SUCCESS);
          }

          if (response.status === 204) {
            log.clearAllDictionaryErrors();
          }
          dispatch({type: UPDATE_OK, response: response});
          callback(null, response);
        })
        .catch((error) => {
          log.debug('doUpdatePassword:  failed ', error);
          if (error.status === 401) {
            log.setDictionaryMessage(ALL_MESSAGES.INCORRECT_OLD_PASSWORD);
            log.clearSuccessMessage(ALL_MESSAGES.NEW_PASSWORD_SET_SUCCESS);
          }
          dispatch({type: UPDATE_FAIL, error});
          callback(error, null);
        });
    };
  },

  doResetPassword: (token, newPassword, callback = () => {
  }) => {
    log.debug('doResetPassword:  newState', token, newPassword);
    return (dispatch, getState) => {
      dispatch({type: UPDATE});
      return CLIENT.resetUserPassword(token, newPassword)
        .then((response) => {
          log.debug('doResetPassword:  success', response);
          if (response.status === 200) {
            log.setDictionaryMessage(ALL_MESSAGES.NEW_PASSWORD_SET_SUCCESS);
          }
          dispatch({type: UPDATE_OK, response: response});
          callback(null, response);
        })
        .catch((error) => {
          dispatch({type: UPDATE_FAIL, error});
          callback(error, null);
        });
    }
  },
  getUserDisplayName: (users, userId) => {
    // TODO: Bit rubbish doing this every single time. Think of a better way.
    const userArray = (users && users.data) || [];
    const userDictionary = userArray.reduce((dictionary, user) => {
      dictionary[user.id] = user;
      return dictionary;
    }, {});
    return userDictionary[userId] && userDictionary[userId].displayName;

  },
  checkPasswordStrength: (password) => {
    return (dispatch, getState) => {
      return getPasswordStrength(password)
        .then((response) => {
          dispatch({type: CHECK_PASSWORD_STRENGTH_OK, response: Object.assign({}, response, {password: password})});
        })
        .catch((error) => {
          dispatch({type: CHECK_PASSWORD_STRENGTH_OK, error: Object.assign({}, error, {password: password})});
        });
    };
  },
  
  doGetRoles: () => {
    return (dispatch, getState) => {
      dispatch({type: LOAD});
      return CLIENT.getRoles()
        .then((response) => {
          log.debug('doGetRoles()', response);
          dispatch({type: ROLE_LOAD_OK, response: response});
        })
        .catch((error) => {
          dispatch({type: ROLE_LOAD_FAIL, error});
        });
    };
  },
};

export default API;

